import * as THREE from 'three';
// Wash for the room while the loader hands over: the room grows outward from the desk, and its edge bleeds from
// paper into colour like ink in water. Fragments not reached yet are discarded (the paper behind the canvas shows
// through), so everything stays opaque and nothing sorts wrongly.
export const dissolveUniforms = {
    uReveal: { value: 1 },
    uOrigin: { value: new THREE.Vector3() },
    uRadius: { value: 40000 }, // farthest distance to cover from the origin
};
const NOISE = /* glsl */ `
varying vec3 vDissolveWorld;
uniform float uReveal;
uniform vec3 uOrigin;
uniform float uRadius;
float dh(vec3 p) { p = fract(p * 0.3183099 + 0.1); p *= 17.0; return fract(p.x * p.y * p.z * (p.x + p.y + p.z)); }
float dnoise(vec3 x) {
    vec3 i = floor(x), f = fract(x); f = f * f * (3.0 - 2.0 * f);
    return mix(mix(mix(dh(i), dh(i + vec3(1, 0, 0)), f.x), mix(dh(i + vec3(0, 1, 0)), dh(i + vec3(1, 1, 0)), f.x), f.y),
               mix(mix(dh(i + vec3(0, 0, 1)), dh(i + vec3(1, 0, 1)), f.x), mix(dh(i + vec3(0, 1, 1)), dh(i + vec3(1, 1, 1)), f.x), f.y), f.z);
}
float dfbm(vec3 p) { return 0.55 * dnoise(p) + 0.3 * dnoise(p * 2.03) + 0.15 * dnoise(p * 4.1); }
`;
const FRAGMENT = /* glsl */ `
#include <encodings_fragment>
if (uReveal < 1.0) {
    const vec3 INK = vec3(0.137, 0.188, 0.180);
    const vec3 PAPER = vec3(0.894, 0.922, 0.910);
    float n = dfbm(vDissolveWorld / 1800.0);
    float fine = dfbm(vDissolveWorld / 450.0);                 // small grain for the edge
    // distance from the desk, roughened by the noise, against a front that moves outward (sea and sky last)
    float d = min(distance(vDissolveWorld, uOrigin) / uRadius, 1.0) + (n - 0.5) * 0.12 + (fine - 0.5) * 0.06;
    float front = uReveal * 1.2 - 0.08;
    float k = smoothstep(front - 0.14, front, d);              // 0 = full colour, 1 = paper
    if (k >= 0.999) discard;
    float bleed = smoothstep(front - 0.14, front - 0.04, d) * (1.0 - smoothstep(front - 0.04, front, d));
    gl_FragColor.rgb = mix(mix(gl_FragColor.rgb, PAPER, k), INK, bleed * 0.35);
}
`;
/** Adds the dissolve to a material (shared uniforms, so one value drives every material). */
export function addDissolve(material) {
    material.onBeforeCompile = (shader) => {
        Object.assign(shader.uniforms, dissolveUniforms);
        shader.vertexShader = 'varying vec3 vDissolveWorld;\n' + shader.vertexShader.replace('#include <project_vertex>', '#include <project_vertex>\nvDissolveWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;');
        shader.fragmentShader = NOISE + shader.fragmentShader.replace('#include <encodings_fragment>', FRAGMENT);
    };
    material.needsUpdate = true;
}
/**
 * A plain fade from paper colour for things that should just appear (the sea and sky): the material stays opaque, so
 * where two surfaces overlap only the nearest one shows, and its colour moves from the paper to its own.
 */
export function addPaperFade(material) {
    const uniform = { value: 1 };
    material.onBeforeCompile = (shader) => {
        shader.uniforms.uPaperFade = uniform;
        shader.fragmentShader = 'uniform float uPaperFade;\n' + shader.fragmentShader.replace('#include <encodings_fragment>', '#include <encodings_fragment>\ngl_FragColor.rgb = mix(vec3(0.894, 0.922, 0.910), gl_FragColor.rgb, uPaperFade);');
    };
    material.needsUpdate = true;
    return uniform;
}
